import React, { useState } from "react";
import { useUserShippingData } from "./useUserShippingData";
import { useAuth } from "../../../contexts/AuthContext";
import Button from "../../../components/controls/Button";

const KG_PER_LB = 0.45359237;
const CM_PER_IN = 2.54;
const CM_PER_FT = 30.48;

const money = (amount, currency = "CAD") =>
  new Intl.NumberFormat("fr-CA", { style: "currency", currency }).format(
    Number(amount) || 0
  );

const fmtLongDateFR = (d) => {
  if (!d) return "—";
  const date = new Date(d); // ISO ou Date
  return date.toLocaleDateString("fr-CA", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const toKg = (v, unit) =>
  unit === "lb" ? (Number(v) || 0) * KG_PER_LB : Number(v) || 0;
const toCm = (v, unit) =>
  unit === "in"
    ? (Number(v) || 0) * CM_PER_IN
    : unit === "ft"
    ? (Number(v) || 0) * CM_PER_FT
    : Number(v) || 0;

// NEW — destination de test + helpers
const DEFAULT_DEST = {
  countryCode: "CA",
  provinceCode: "QC",
  city: "Montréal",
  postalCode: "H1V0B2",
};
const normalizeTo = (to = {}) => {
  const countryCode = (to.countryCode || to.country || "CA").toUpperCase();
  const provinceCode = to.provinceCode || to.stateOrProvince || "";
  const city = to.city || "";
  const postalCode = (to.postalCode || to.postalOrZip || "")
    .toUpperCase()
    .replace(/[ -]/g, "");
  return { countryCode, provinceCode, city, postalCode };
};
const formatDest = (to) => {
  const parts = [
    to.city,
    [to.stateOrProvince, to.postalOrZip].filter(Boolean).join(" "),
    to.country, // affichera "CA" (OK pour un bandeau)
  ].filter(Boolean);
  return parts.join(", ");
};

export default function ShippingSection({ formData, onChange }) {
  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(false);
  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const { currentUser } = useAuth();
  const currentUserId = currentUser?.id;

  const { carriers, warehouses } = useUserShippingData(API_BASE, currentUserId);
  const weightUnit = formData.weightUnit || "kg";
  const lengthUnit = formData.lengthUnit || "cm";
  const display = {
    weight:
      weightUnit === "lb"
        ? (formData.weightKg ?? 0) / KG_PER_LB
        : formData.weightKg ?? 0,
    L:
      lengthUnit === "in"
        ? (formData.lengthCm ?? 0) / CM_PER_IN
        : lengthUnit === "ft"
        ? (formData.lengthCm ?? 0) / CM_PER_FT
        : formData.lengthCm ?? 0,
    W:
      lengthUnit === "in"
        ? (formData.widthCm ?? 0) / CM_PER_IN
        : lengthUnit === "ft"
        ? (formData.widthCm ?? 0) / CM_PER_FT
        : formData.widthCm ?? 0,
    H:
      lengthUnit === "in"
        ? (formData.heightCm ?? 0) / CM_PER_IN
        : lengthUnit === "ft"
        ? (formData.heightCm ?? 0) / CM_PER_FT
        : formData.heightCm ?? 0,
  };

  // NEW — on fige la destination de test pour l'appel
  const destForPricing = normalizeTo(DEFAULT_DEST);

  const getRates = async () => {
    setLoading(true);
    setRates(null);
    try {
      // (optionnel) gardes-toi une valve si le transporteur/entrepôt n’est pas choisi
      if (!formData.carrierAccountId || !formData.warehouseId) {
        alert("Sélectionnez un transporteur et un entrepôt.");
        return;
      }

      const payload = {
        carrierAccountId: formData.carrierAccountId || null,
        warehouseId: formData.warehouseId || null,
        to: destForPricing, // NEW — adresse de test envoyée au backend
        weightKg: Number(formData.weightKg) || 0,
        lengthCm: Number(formData.lengthCm) || 0,
        widthCm: Number(formData.widthCm) || 0,
        heightCm: Number(formData.heightCm) || 0,
      };

      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/shipping/rates`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setRates(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Erreur getRates", err);
      setRates([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-white/70 rounded-lg p-4 border space-y-5">
      <h2 className="text-lg font-semibold text-primary">Expédition</h2>

      {/* Transporteur & Entrepôt */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm mb-1 text-primary">
            Transporteur
          </label>
          <select
            className="select select-bordered w-full bg-base-100"
            value={formData.carrierAccountId || ""}
            onChange={(e) => onChange("carrierAccountId", e.target.value)}
          >
            <option value=""></option>
            {carriers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.accountNickname ?? c.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm mb-1 text-primary">
            Entrepôt d’envoi
          </label>
          <select
            className="select select-bordered w-full bg-base-100"
            value={formData.warehouseId || ""}
            onChange={(e) => onChange("warehouseId", e.target.value || null)}
          >
            <option value=""></option>
            {(warehouses || []).map((w) => {
              const id = String(w.id ?? w.Id ?? "");
              const label =
                w.label ??
                w.name ??
                w.Name ??
                w.addressFull ??
                w.AddressFull ??
                "(Entrepôt)";
              return (
                <option key={id} value={id}>
                  {label}
                </option>
              );
            })}
          </select>
        </div>
      </div>

      {/* Poids */}
      <div>
        <label className="block text-sm mb-1 text-primary">Poids</label>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 items-center">
          <div className="col-span-2 md:col-span-4">
            <input
              type="number"
              min="0"
              step="0.01"
              className="input input-bordered w-full bg-base-100"
              value={display.weight || ""}
              onChange={(e) =>
                onChange("weightKg", toKg(e.target.value, weightUnit))
              }
            />
          </div>
          <div className="col-span-1 md:col-span-2">
            <select
              className="select select-bordered w-full bg-base-100"
              value={weightUnit}
              onChange={(e) => onChange("weightUnit", e.target.value)}
            >
              <option value="kg">kg</option>
              <option value="lb">lb</option>
            </select>
          </div>
        </div>
      </div>

      {/* Dimensions */}
      <div>
        <label className="block text-sm mb-2 text-primary">Dimensions</label>
        <div className="grid grid-cols-3 gap-2">
          {[
            { key: "lengthCm", label: "Longueur", val: display.L },
            { key: "widthCm", label: "Largeur", val: display.W },
            { key: "heightCm", label: "Hauteur", val: display.H },
          ].map((f) => (
            <div key={f.key} className="flex flex-col">
              <span className="text-xs text-primary mb-1">{f.label}</span>
              <input
                type="number"
                min="0"
                step="0.01"
                className="input input-bordered w-full bg-base-100"
                value={f.val || ""}
                onChange={(e) =>
                  onChange(f.key, toCm(e.target.value, lengthUnit))
                }
              />
            </div>
          ))}
        </div>
        <div className="mt-2">
          <select
            className="select select-bordered bg-base-100"
            value={lengthUnit}
            onChange={(e) => onChange("lengthUnit", e.target.value)}
          >
            <option value="cm">cm</option>
            <option value="in">pouces (in)</option>
            <option value="ft">pieds (ft)</option>
          </select>
        </div>
      </div>

      {/* Bandeau adresse de test */}
      <p className="text-xs text-gray-600">
        Adresse utilisée pour calculer vos tarifs :{" "}
        <b>{formatDest(destForPricing)}</b>
      </p>

      {/* Bouton */}
      <div className="flex justify-end">
        <Button
          as="button"
          type="button"
          onClick={getRates}
          color="primary" // ton dégradé jaune→vert
          size="md"
          disabled={loading}
          className="px-4 py-2" // optionnel (le padding est déjà géré par size)
          aria-busy={loading ? "true" : "false"}
        >
          {loading ? "Calcul..." : "Obtenir les tarifs"}
        </Button>
      </div>

      {/* Résultats */}
      {rates && (
        <div className="mt-2">
          <h3 className="font-medium mb-2">
            Options & tarifs (Adresse test: H1V 0B2, Montréal, QC) :
          </h3>

          {rates.length === 0 ? (
            <div className="text-sm text-gray-500">Aucun tarif retourné.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[...rates]
                .sort((a, b) => (a.total ?? 0) - (b.total ?? 0))
                .map((r, i) => {
                  const best = i === 0;
                  return (
                    <div
                      key={`${r.carrier}-${r.serviceCode}-${i}`}
                      className={`rounded-xl border p-4 shadow-sm bg-white ${
                        best
                          ? "border-emerald-400 ring-1 ring-emerald-200 bg-emerald-50"
                          : "border-gray-200"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-sm text-gray-600">
                          {r.carrier || "—"}
                        </div>
                        {best && (
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">
                            Meilleur prix
                          </span>
                        )}
                      </div>
                      <div className="text-base font-semibold">
                        {r.serviceName}
                      </div>

                      <div className="mt-2 text-2xl font-bold">
                        {money(r.total, r.currency || "CAD")}
                      </div>

                      <div className="mt-1 text-sm text-gray-600">
                        Arrivée estimée :{" "}
                        {fmtLongDateFR(r.expectedDeliveryDate)}
                      </div>

                      {/* (optionnel) bouton de sélection */}
                      {/* <button className="mt-3 w-full btn btn-sm">Choisir</button> */}
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
